/**
 * @module services/catalogs/types
 * @description Common types for all catalog services
 */

import type { ItemData } from '@types';

/**
 * Base record interface for all catalog services
 */
export interface BaseCatalogRecord {
  /** Unique identifier from the catalog */
  id: string;
  /** Parsed item data ready for Zotero */
  item: Partial<ItemData>;
  /** Link to the catalog page */
  catalogUrl?: string;
}

/**
 * Base search response interface for all catalog services
 */
export interface BaseCatalogSearchResponse<T extends BaseCatalogRecord = BaseCatalogRecord> {
  /** Total number of results found */
  totalResults: number;
  /** Array of parsed records */
  records: T[];
}

/**
 * Common search options
 */
export interface BaseSearchOptions {
  /** Starting record (1-based for SRU, 0-based for REST APIs) */
  startRecord?: number;
  /** Maximum number of records to return */
  maximumRecords?: number;
}

/**
 * SRU-specific search options
 */
export interface SRUSearchOptions extends BaseSearchOptions {
  /** Record schema to request (e.g., 'unimarc', 'dublincore', 'dc') */
  recordSchema?: string;
}

/**
 * Common language code mappings (ISO 639-3 to ISO 639-1 or full names)
 */
export const LANGUAGE_MAP: Record<string, string> = {
  // ISO 639-3 to ISO 639-1
  fre: 'fr',
  fra: 'fr',
  eng: 'en',
  ger: 'de',
  deu: 'de',
  spa: 'es',
  ita: 'it',
  por: 'pt',
  rus: 'ru',
  ara: 'ar',
  chi: 'zh',
  zho: 'zh',
  jpn: 'ja',
  lat: 'la',
  gre: 'el',
  ell: 'el',
  dut: 'nl',
  nld: 'nl',
  pol: 'pl',
  cze: 'cs',
  ces: 'cs',
  hun: 'hu',
  tur: 'tr',
  kor: 'ko',
  heb: 'he',
  swe: 'sv',
  nor: 'no',
  dan: 'da',
  fin: 'fi',
  roh: 'rm', // Romansh (Swiss)
  // French names
  français: 'fr',
  french: 'fr',
  anglais: 'en',
  english: 'en',
  allemand: 'de',
  german: 'de',
  espagnol: 'es',
  spanish: 'es',
  italien: 'it',
  italian: 'it',
  portugais: 'pt',
  portuguese: 'pt',
  russe: 'ru',
  russian: 'ru',
  arabe: 'ar',
  arabic: 'ar',
  chinois: 'zh',
  chinese: 'zh',
  japonais: 'ja',
  japanese: 'ja',
  latin: 'la',
  greek: 'el',
};

/**
 * Extended language map with full names (for Open Library style output)
 */
export const LANGUAGE_FULL_NAMES: Record<string, string> = {
  eng: 'English',
  fre: 'French',
  fra: 'French',
  ger: 'German',
  deu: 'German',
  spa: 'Spanish',
  ita: 'Italian',
  por: 'Portuguese',
  rus: 'Russian',
  chi: 'Chinese',
  zho: 'Chinese',
  jpn: 'Japanese',
  ara: 'Arabic',
  hin: 'Hindi',
  kor: 'Korean',
  dut: 'Dutch',
  nld: 'Dutch',
  swe: 'Swedish',
  nor: 'Norwegian',
  dan: 'Danish',
  fin: 'Finnish',
  pol: 'Polish',
  cze: 'Czech',
  ces: 'Czech',
  hun: 'Hungarian',
  tur: 'Turkish',
  gre: 'Greek',
  ell: 'Greek',
  heb: 'Hebrew',
  lat: 'Latin',
};

/**
 * Local proxy paths for SRU catalog services.
 * These are handled by Vercel rewrites (production) and Vite dev proxy (development).
 * This eliminates the dependency on unreliable third-party CORS proxy services.
 */
export const CATALOG_PROXY_PATHS = {
  sudoc: '/api/catalog/sudoc',
  bnf: '/api/catalog/bnf',
  swisscovery: '/api/catalog/swisscovery',
} as const;

/**
 * System fields that are always valid regardless of item type
 */
export const SYSTEM_FIELDS = [
  'itemType',
  'key',
  'version',
  'dateAdded',
  'dateModified',
  'creators',
  'tags',
  'collections',
  'relations',
] as const;
